import * as DialogPrimitive from '@radix-ui/react-dialog';
import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/Domain/components/Shared/Button/Button';

interface SiteScrapeDiscardConfirmProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  returnFocusRef: RefObject<HTMLButtonElement | null>;
}

// Rendered inside the review card (no portal), so it dims only the card the way SheetConfirmationCloseDialog does.
export const SiteScrapeDiscardConfirm = ({
  open,
  onOpenChange,
  onConfirm,
  returnFocusRef,
}: SiteScrapeDiscardConfirmProps) => {
  const { t } = useTranslation(['knowledge_base']);

  // Nested parts inherit the review card's force-mounted portal, so they'd stay mounted while closed.
  if (!open) return null;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Overlay className="absolute inset-0 z-20 rounded-2xl bg-black/30 backdrop-blur-sm" />
      <DialogPrimitive.Content
        role="alertdialog"
        aria-modal="true"
        className="absolute inset-0 z-30 flex items-center justify-center p-6 focus:outline-none"
        data-testid="site-scrape-review-discard-confirm"
        // No Trigger to return to: Radix would focus nothing, losing the keyboard user's place.
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          returnFocusRef.current?.focus();
        }}
      >
        <div className="flex w-full max-w-sm flex-col gap-4 rounded-xl border border-border bg-card p-5 text-foreground shadow-lg">
          <div className="flex flex-col gap-1.5">
            <DialogPrimitive.Title className="text-base font-semibold text-foreground">
              {t('tabs.website.review.discard_confirm_title')}
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="text-sm text-muted-foreground">
              {t('tabs.website.review.discard_confirm_description')}
            </DialogPrimitive.Description>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <DialogPrimitive.Close asChild>
              <Button
                variant="secondary"
                size="sm"
                data-testid="site-scrape-review-discard-confirm-keep"
              >
                {t('tabs.website.review.discard_confirm_keep')}
              </Button>
            </DialogPrimitive.Close>
            <Button
              variant="danger"
              size="sm"
              onClick={onConfirm}
              data-testid="site-scrape-review-discard-confirm-action"
            >
              {t('tabs.website.review.discard_confirm_action')}
            </Button>
          </div>
        </div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Root>
  );
};
